// Redshirt ratio utilities and defaults
// Ratio is defined as redshirts / (members + captains)

export type RatioSeverity = 'success' | 'warn' | 'danger'

export interface RedshirtRatioConfig {
  // Green band inclusive [greenMin, greenMax]
  greenMin: number // 1:5 -> 0.2
  greenMax: number // 1:4 -> 0.25
  // Yellow low band [yellowLowMin, greenMin)
  yellowLowMin: number // 1:10 -> 0.1
  // Yellow high band (greenMax, yellowHighMax]
  yellowHighMax: number // 1:3 -> 0.333...
  // Red below yellowLowMin and above yellowHighMax
  minAbsolute: number // minimum redshirts per unit (absolute floor), suggestion aid
}

export const REDSHIRT_RATIO_DEFAULTS: RedshirtRatioConfig = {
  greenMin: 1 / 5, // 0.2
  greenMax: 1 / 4, // 0.25
  yellowLowMin: 1 / 10, // 0.1
  yellowHighMax: 1 / 3, // ~0.3333
  minAbsolute: 2,
}

export function useRedshirtRatio(custom?: Partial<RedshirtRatioConfig>) {
  const cfg: RedshirtRatioConfig = { ...REDSHIRT_RATIO_DEFAULTS, ...(custom || {}) }

  const computeRedshirtRatio = (redshirts: number, membersPlusCaptains: number): number => {
    const denom = Math.max(0, membersPlusCaptains)
    if (denom <= 0) return 0
    const r = redshirts / denom
    if (!Number.isFinite(r) || r < 0) return 0
    return r
  }

  const getRedshirtRatioSeverity = (ratio: number, redshirts?: number): RatioSeverity => {
    // Base severity by ratio bands
    let severity: RatioSeverity
    if (ratio < cfg.yellowLowMin) severity = 'danger' // too few redshirts by ratio
    else if (ratio > cfg.yellowHighMax) severity = 'danger' // too many redshirts by ratio
    else if ((ratio >= cfg.yellowLowMin && ratio < cfg.greenMin) || (ratio > cfg.greenMax && ratio <= cfg.yellowHighMax)) severity = 'warn'
    else severity = 'success'

    // Absolute floor: if otherwise green but below minimum absolute count, downgrade to warn
    if (severity === 'success' && typeof redshirts === 'number' && redshirts < cfg.minAbsolute) {
      severity = 'warn'
    }
    return severity
  }

  const formatRedshirtRatio = (ratio: number): string => {
    // Display as 1:K with one decimal (avoid divide-by-zero)
    if (ratio <= 0) return 'None'
    const k = 1 / ratio
    // Clamp to a reasonable range for display
    const clamped = Math.min(999, Math.max(0, k))
    return `1:${clamped.toFixed(1)}`
  }

  const formatPercent = (ratio: number): string => `${Math.round(ratio * 100)}%`

  const getSuggestedRedshirtCounts = (membersPlusCaptains: number) => {
    const minByGreen = Math.ceil(membersPlusCaptains * cfg.greenMin)
    const maxByGreen = Math.floor(membersPlusCaptains * cfg.greenMax)
    const min = Math.max(cfg.minAbsolute, minByGreen)
    const max = Math.max(min, maxByGreen)
    return { min, max, range: [min, max] as [number, number] }
  }

  // Human-readable legend derived from current thresholds so UI never drifts
  const getLegendText = (): string => {
    const k = (ratio: number): string => {
      if (ratio <= 0) return '∞'
      const v = 1 / ratio
      return `${Math.round(v)}`
    }
    const green = `1:${k(cfg.greenMin)}–1:${k(cfg.greenMax)}`
    const warnLow = `1:${k(cfg.yellowLowMin)}–1:${k(cfg.greenMin)}`
    const warnHigh = `1:${k(cfg.yellowHighMax)}–1:${k(cfg.greenMax)}`
    const danger = `<1:${k(cfg.yellowLowMin)} or >1:${k(cfg.yellowHighMax)}`
    return `Green: ${green} • Warn: ${warnLow} or ${warnHigh} • Danger: ${danger} • Denominator: members + captains`
  }

  return {
    config: cfg,
    computeRedshirtRatio,
    getRedshirtRatioSeverity,
    formatRedshirtRatio,
    formatPercent,
    getSuggestedRedshirtCounts,
    getLegendText,
  }
}
